import { useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, ChevronDown, ChevronRight, XCircle } from "lucide-react";
import * as React from "react";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { SearchInput } from "@/components/ui/search-input";
import { Textarea } from "@/components/ui/textarea";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { get, post } from "@/lib/api/_client";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./tool-playground.i18n";

type JsonSchemaProp = {
  type?: string;
  description?: string;
  enum?: Array<string | number>;
  items?: {
    type?: string;
    enum?: Array<string | number>;
    properties?: Record<string, JsonSchemaProp>;
    required?: string[];
  };
  properties?: Record<string, JsonSchemaProp>;
  required?: string[];
};

/** Build an example skeleton from a JSON schema for use as a placeholder. */
function buildSkeleton(prop: JsonSchemaProp): string {
  if (prop.type === "array") {
    if (prop.items?.properties) {
      const obj: Record<string, unknown> = {};
      for (const [k, sub] of Object.entries(prop.items.properties)) {
        obj[k] = sub.enum?.[0] ?? exampleValue(sub.type);
      }
      return JSON.stringify([obj], null, 2);
    }
    if (prop.items?.enum?.length) {
      return JSON.stringify([prop.items.enum[0]], null, 2);
    }
    return "[]";
  }
  if (prop.type === "object") {
    if (prop.properties) {
      const obj: Record<string, unknown> = {};
      for (const [k, sub] of Object.entries(prop.properties)) {
        obj[k] = sub.enum?.[0] ?? exampleValue(sub.type);
      }
      return JSON.stringify(obj, null, 2);
    }
    return "{}";
  }
  return "";
}

function exampleValue(type: string | undefined): unknown {
  if (type === "number" || type === "integer") return 0;
  if (type === "boolean") return false;
  if (type === "array") return [];
  if (type === "object") return {};
  return "";
}

/** Compact one-line summary of an item schema, e.g. `{ typeId: string, amount: number }`. */
function summariseItemShape(prop: JsonSchemaProp): string | null {
  const props = prop.items?.properties || prop.properties;
  if (!props) return null;
  const required = new Set(prop.items?.required || prop.required || []);
  const parts = Object.entries(props).map(([k, sub]) => {
    const t = sub.enum ? sub.enum.map((e) => `"${e}"`).join("|") : sub.type || "any";
    return `${k}${required.has(k) ? "" : "?"}: ${t}`;
  });
  return `{ ${parts.join(", ")} }`;
}

type ToolInfo = {
  name: string;
  description: string;
  size: "small" | "large";
  server?: string; // MCP server ID (for external tools)
  parameters: {
    type: "object";
    properties: Record<string, JsonSchemaProp>;
    required?: string[];
  };
};

const TOKEN_LIMITS = { small: 3000, large: 15000 };

const DESCRIPTION_TRUNCATE_CHARS = 160;

/** Long descriptions push the form below the fold; collapse by default. */
function ToolDescription({ text }: { text: string }) {
  const [expanded, setExpanded] = useState(false);
  const isLong = text.length > DESCRIPTION_TRUNCATE_CHARS;
  if (!isLong) {
    return <p className="text-xs text-muted-foreground">{text}</p>;
  }
  return (
    <button
      type="button"
      onClick={() => setExpanded((v) => !v)}
      className="flex w-full items-start gap-1 text-left text-xs text-muted-foreground hover:text-foreground"
      aria-expanded={expanded}
    >
      {expanded ? (
        <ChevronDown className="mt-0.5 h-3 w-3 flex-shrink-0" />
      ) : (
        <ChevronRight className="mt-0.5 h-3 w-3 flex-shrink-0" />
      )}
      <span className="flex-1">
        {expanded ? text : `${text.slice(0, DESCRIPTION_TRUNCATE_CHARS).trimEnd()}…`}
      </span>
    </button>
  );
}

const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/g;

/**
 * Wrap each UUID in the result text with a span the user can click to copy.
 * Tool chains (overview → create on a returned id) become much smoother when
 * IDs aren't a copy-paste-the-right-substring chore.
 */
function renderResultText(text: string, copyLabel: string): React.ReactNode {
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  UUID_RE.lastIndex = 0;
  for (;;) {
    const match = UUID_RE.exec(text);
    if (match === null) break;
    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
    const id = match[0];
    parts.push(
      // The key belongs on the outer element, since that is what goes into
      // `parts`. `asChild` also means the trigger must have exactly one child —
      // a stray text node here makes Slot throw.
      <Tooltip key={`${match.index}-${id}`}>
        <TooltipTrigger asChild>
          <button
            type="button"
            className="cursor-pointer rounded bg-primary/10 px-0.5 underline-offset-2 hover:bg-primary/20 hover:underline"
            onClick={() => {
              navigator.clipboard?.writeText(id).catch(() => {});
            }}
          >
            {id}
          </button>
        </TooltipTrigger>
        <TooltipContent>{copyLabel}</TooltipContent>
      </Tooltip>,
    );
    lastIndex = match.index + id.length;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts.length > 0 ? parts : text;
}

type ClientOption = { id: string; name: string };

interface ToolPlaygroundProps {
  clientId?: string | null;
  clientName?: string | null;
}

// ─── Argument input renderer ─────────────────────────────────
// Picks a control based on JSON-schema shape:
//   string + enum         → native <select> dropdown
//   array + items.enum    → list of <Checkbox> (multi-select)
//   boolean               → single <Checkbox>
//   number / integer      → numeric <Input>
//   array (no enum)       → JSON textarea (Input fallback)
//   anything else         → text <Input>
function renderField(
  key: string,
  prop: JsonSchemaProp,
  value: unknown,
  onChange: (v: unknown) => void,
) {
  // String enum → dropdown
  if (prop.type === "string" && prop.enum && prop.enum.length > 0) {
    return (
      <select
        className="w-full h-8 rounded-md border bg-background px-2 text-xs"
        value={typeof value === "string" ? value : ""}
        onChange={(e) => onChange(e.target.value || undefined)}
      >
        <option value="">— select —</option>
        {prop.enum.map((opt) => (
          <option key={String(opt)} value={String(opt)}>
            {String(opt)}
          </option>
        ))}
      </select>
    );
  }

  // Array of enum → checkbox group
  if (prop.type === "array" && prop.items?.enum && prop.items.enum.length > 0) {
    const selected = Array.isArray(value) ? (value as Array<string | number>).map(String) : [];
    return (
      <div className="flex flex-wrap gap-x-3 gap-y-1.5 px-2 py-1.5 border rounded-md bg-background">
        {prop.items.enum.map((opt) => {
          const optStr = String(opt);
          const checked = selected.includes(optStr);
          const id = `${key}-${optStr}`;
          return (
            <label
              key={optStr}
              htmlFor={id}
              className="flex items-center gap-1.5 text-xs cursor-pointer"
            >
              <Checkbox
                id={id}
                checked={checked}
                onCheckedChange={(c) => {
                  const next = c ? [...selected, optStr] : selected.filter((s) => s !== optStr);
                  onChange(next);
                }}
              />
              <span>{optStr}</span>
            </label>
          );
        })}
      </div>
    );
  }

  // Boolean → checkbox
  if (prop.type === "boolean") {
    const checked = value === true;
    const id = `${key}-bool`;
    return (
      <label htmlFor={id} className="flex items-center gap-2 text-xs cursor-pointer">
        <Checkbox id={id} checked={checked} onCheckedChange={(c) => onChange(c === true)} />
        <span className="text-muted-foreground">{checked ? "true" : "false"}</span>
      </label>
    );
  }

  // Number / integer → numeric input
  if (prop.type === "number" || prop.type === "integer") {
    return (
      <Input
        type="number"
        className="h-8 text-xs"
        placeholder={prop.description || key}
        value={typeof value === "number" || typeof value === "string" ? String(value) : ""}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw === "") return onChange(undefined);
          const n = Number(raw);
          onChange(Number.isFinite(n) ? n : raw);
        }}
      />
    );
  }

  // Array / object → multi-line JSON textarea with schema-driven skeleton
  // placeholder, an items/shape hint, and live JSON parse feedback.
  if (prop.type === "array" || prop.type === "object") {
    const raw =
      typeof value === "string" ? value : value !== undefined ? JSON.stringify(value, null, 2) : "";
    let parseError: string | null = null;
    if (raw.trim()) {
      try {
        JSON.parse(raw);
      } catch (e) {
        parseError = (e as Error).message;
      }
    }
    const itemShape = summariseItemShape(prop);
    const skeleton = buildSkeleton(prop);
    return (
      <div className="space-y-1">
        <Textarea
          className="text-xs font-mono min-h-[80px] resize-y"
          placeholder={skeleton || prop.description || `JSON ${prop.type}`}
          value={raw}
          onChange={(e) => onChange(e.target.value)}
          spellCheck={false}
        />
        {itemShape && (
          <div className="text-[10px] text-muted-foreground/70 font-mono">
            {prop.type === "array" ? "items: " : "shape: "}
            {itemShape}
          </div>
        )}
        {parseError && <div className="text-[10px] text-destructive">JSON error: {parseError}</div>}
      </div>
    );
  }

  // Default → text input
  return (
    <Input
      className="h-8 text-xs"
      placeholder={prop.description || key}
      value={typeof value === "string" ? value : value !== undefined ? String(value) : ""}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

export function ToolPlayground({ clientId, clientName }: ToolPlaygroundProps) {
  const t = useT(T);
  const queryClient = useQueryClient();
  const [tools, setTools] = useState<ToolInfo[]>([]);
  const [selectedTool, setSelectedTool] = useState<string>("");
  // args holds values in their native JSON shape (string/number/boolean/array).
  // The render layer below picks an input control per JSON-schema property.
  const [args, setArgs] = useState<Record<string, unknown>>({});
  const [resultText, setResultText] = useState<string | null>(null);
  const [resultError, setResultError] = useState<string | null>(null);
  const [tokenEstimate, setTokenEstimate] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [durationMs, setDurationMs] = useState<number | null>(null);

  // Context state
  const [contextMode, setContextMode] = useState<"global" | "client" | "system" | "mcp">(
    clientId ? "client" : "global",
  );
  const [selectedClientId, setSelectedClientId] = useState<string | null>(clientId ?? null);
  const [selectedClientName, setSelectedClientName] = useState<string | null>(clientName ?? null);
  const [clientSearch, setClientSearch] = useState("");
  const [clientOptions, setClientOptions] = useState<ClientOption[]>([]);
  const [showClientPicker, setShowClientPicker] = useState(false);

  // Reload tools when scope changes
  useEffect(() => {
    const scope =
      contextMode === "mcp"
        ? "mcp"
        : contextMode === "system"
          ? "system"
          : contextMode === "client" && selectedClientId
            ? "client"
            : "global";
    get<{ tools: ToolInfo[] }>(`/internal/ai/tools?scope=${scope}`)
      .then((data) => {
        setTools(data.tools);
        setSelectedTool("");
        setArgs({});
        setResultText(null);
        setResultError(null);
      })
      .catch((err) => {
        setTools([]);
        setResultError(err instanceof Error ? err.message : t.loadToolsFailed);
      });
  }, [contextMode, selectedClientId]);

  // Sync auto-detected client
  useEffect(() => {
    if (clientId) {
      setSelectedClientId(clientId);
      setSelectedClientName(clientName ?? null);
      setContextMode("client");
    }
  }, [clientId, clientName]);

  // Load all clients for picker
  const [allClients, setAllClients] = useState<ClientOption[]>([]);
  useEffect(() => {
    get<Array<{ id: string; name: string }>>("/internal/clients")
      .then((data) => setAllClients(data.map((c) => ({ id: c.id, name: c.name }))))
      .catch(() => {});
  }, []);

  // Filter clients by search
  useEffect(() => {
    if (!showClientPicker) return;
    const q = clientSearch.toLowerCase();
    setClientOptions(
      q
        ? allClients.filter((c) => c.name.toLowerCase().includes(q)).slice(0, 10)
        : allClients.slice(0, 10),
    );
  }, [clientSearch, showClientPicker, allClients]);

  const currentTool = tools.find((t) => t.name === selectedTool);
  const activeClientId = contextMode === "client" ? selectedClientId : null;

  const handleRun = async () => {
    if (!selectedTool) return;
    setLoading(true);
    setResultText(null);
    setResultError(null);
    setDurationMs(null);

    try {
      // args are already stored in their native JSON shape; only thing left is
      // to drop empty/blank entries and parse JSON-string-typed array/object
      // params (the textarea fallback for arrays without an enum).
      const parsedArgs: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(args)) {
        if (value === "" || value === undefined || value === null) continue;
        if (Array.isArray(value) && value.length === 0) continue;
        const propType = currentTool?.parameters.properties[key]?.type;
        if ((propType === "array" || propType === "object") && typeof value === "string") {
          try {
            parsedArgs[key] = JSON.parse(value);
          } catch {
            parsedArgs[key] = value;
          }
          continue;
        }
        parsedArgs[key] = value;
      }

      const resp = await post<{
        success: boolean;
        text?: string;
        error?: string;
        durationMs: number;
        tokenEstimate?: number;
        invalidateKeys?: string[][];
        deletedFlowInstanceId?: string;
      }>("/internal/ai/tools/test", {
        name: selectedTool,
        args: parsedArgs,
        ...(activeClientId ? { clientId: activeClientId } : {}),
        ...(currentTool?.server ? { mcpServerId: currentTool.server } : {}),
      });
      setDurationMs(resp.durationMs);
      setTokenEstimate(resp.tokenEstimate ?? null);
      if (!resp.success) {
        setResultError(resp.error || t.executionFailed);
      } else {
        setResultText(resp.text || t.done);
        // Invalidate React Query caches so the UI updates
        if (resp.invalidateKeys?.length) {
          for (const key of resp.invalidateKeys) {
            queryClient.invalidateQueries({ queryKey: key });
          }
        }
      }
    } catch (error) {
      setResultError(`Error: ${error}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Context toggle */}
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-xs">
          <span className="text-muted-foreground">{t.context}</span>
          <button
            type="button"
            className={`px-2 py-1 rounded-md transition-colors ${contextMode === "global" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`}
            onClick={() => setContextMode("global")}
          >
            {t.global}
          </button>
          <button
            type="button"
            className={`px-2 py-1 rounded-md transition-colors ${contextMode === "client" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`}
            onClick={() => {
              setContextMode("client");
              if (!selectedClientId) setShowClientPicker(true);
            }}
          >
            {t.client}
            {selectedClientName && contextMode === "client" ? `: ${selectedClientName}` : ""}
          </button>
          <button
            type="button"
            className={`px-2 py-1 rounded-md transition-colors ${contextMode === "system" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`}
            onClick={() => setContextMode("system")}
          >
            {t.system}
          </button>
          <button
            type="button"
            className={`px-2 py-1 rounded-md transition-colors ${contextMode === "mcp" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`}
            onClick={() => setContextMode("mcp")}
          >
            {t.mcp}
          </button>
        </div>

        {/* Client picker */}
        {contextMode === "client" && (showClientPicker || !selectedClientId) && (
          <div className="relative">
            <SearchInput
              inputClassName="h-8 text-xs"
              placeholder={t.searchClient}
              value={clientSearch}
              onValueChange={(value) => {
                setClientSearch(value);
                setShowClientPicker(true);
              }}
              onFocus={() => setShowClientPicker(true)}
            />
            {showClientPicker && clientOptions.length > 0 && (
              <div className="absolute z-50 w-full mt-1 border rounded-md bg-popover shadow-md max-h-[200px] overflow-y-auto">
                {clientOptions.map((c) => (
                  <button
                    type="button"
                    key={c.id}
                    className="w-full text-left px-3 py-2 text-xs hover:bg-muted transition-colors"
                    onClick={() => {
                      setSelectedClientId(c.id);
                      setSelectedClientName(c.name);
                      setClientSearch("");
                      setShowClientPicker(false);
                    }}
                  >
                    {c.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Tool selector */}
      <div>
        <div className="text-xs font-medium text-muted-foreground mb-1">{t.tool}</div>
        <select
          className="w-full rounded-md border bg-background px-3 py-2 text-sm"
          value={selectedTool}
          onChange={(e) => {
            setSelectedTool(e.target.value);
            setArgs({});
            setResultText(null);
            setResultError(null);
          }}
        >
          <option value="">{t.selectTool}</option>
          {tools.map((t) => (
            <option key={t.name} value={t.name}>
              {t.name}
            </option>
          ))}
        </select>
      </div>

      {currentTool && (
        <>
          <ToolDescription text={currentTool.description} />

          {Object.entries(currentTool.parameters.properties).length > 0 && (
            <div className="space-y-2">
              <div className="text-xs font-medium text-muted-foreground">{t.arguments}</div>
              {Object.entries(currentTool.parameters.properties).map(([key, prop]) => (
                <div key={key}>
                  <div className="text-[11px] text-muted-foreground/70 mb-0.5">
                    {key}
                    {currentTool.parameters.required?.includes(key) && (
                      <span className="text-destructive ml-0.5">*</span>
                    )}
                    {prop.type && (
                      <span className="ml-1 text-muted-foreground/50">({prop.type})</span>
                    )}
                  </div>
                  {renderField(key, prop, args[key], (v) => setArgs({ ...args, [key]: v }))}
                  {prop.description && (
                    <div className="text-[10px] text-muted-foreground/50 mt-0.5">
                      {prop.description}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          <Button onClick={handleRun} size="sm" className="w-full gap-2" loading={loading}>
            {t.run}
          </Button>
        </>
      )}

      {/* Error */}
      {resultError && (
        <div className="rounded-md bg-destructive/10 p-3">
          <div className="mb-1 flex items-center gap-1.5 text-xs font-medium text-destructive">
            <XCircle className="h-3.5 w-3.5" />
            <span>{t.error}</span>
            {durationMs != null && (
              <span className="ml-auto text-[10px] font-normal text-muted-foreground/60">
                {durationMs}ms
              </span>
            )}
          </div>
          <div className="text-xs text-destructive">{resultError}</div>
        </div>
      )}

      {/* Text result */}
      {resultText && (
        <div>
          <div className="mb-1 flex items-center justify-between">
            <div className="flex items-center gap-1.5 text-xs font-medium text-success">
              <CheckCircle2 className="h-3.5 w-3.5" />
              <span>{t.success}</span>
            </div>
            <div className="flex items-center gap-2 text-[10px] text-muted-foreground/60">
              {tokenEstimate != null && (
                <span>
                  ~{tokenEstimate} {t.tokens}
                </span>
              )}
              {durationMs != null && <span>{durationMs}ms</span>}
            </div>
          </div>
          {/* Token usage bar */}
          {tokenEstimate != null && currentTool && (
            <div className="mb-2">
              <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${
                    tokenEstimate > TOKEN_LIMITS[currentTool.size] * 0.9
                      ? "bg-destructive"
                      : tokenEstimate > TOKEN_LIMITS[currentTool.size] * 0.6
                        ? "bg-warning"
                        : "bg-success"
                  }`}
                  style={{
                    width: `${Math.min(100, (tokenEstimate / TOKEN_LIMITS[currentTool.size]) * 100)}%`,
                  }}
                />
              </div>
              <div className="flex justify-between text-[9px] text-muted-foreground/50 mt-0.5">
                <span>{currentTool.size} tool</span>
                <span>
                  {tokenEstimate} / {TOKEN_LIMITS[currentTool.size]}
                </span>
              </div>
            </div>
          )}
          <pre className="text-xs bg-muted rounded-md p-3 overflow-auto max-h-[60vh] whitespace-pre-wrap break-all">
            {renderResultText(resultText, t.clickToCopy)}
          </pre>
        </div>
      )}
    </div>
  );
}
